import React from "react";
import { find, propEq } from "ramda";
import { makeStyles } from "@material-ui/core/styles";

import { firstError } from "util/validation";

import AppBar from "@material-ui/core/AppBar";
import Button from "@material-ui/core/Button";
import Dialog from "@material-ui/core/Dialog";
import Paper from "@material-ui/core/Paper";
import Slide from "@material-ui/core/Slide";
import Toolbar from "@material-ui/core/Toolbar";
import TextField from "@material-ui/core/TextField";
import Autocomplete from "@material-ui/lab/Autocomplete";
import Typography from "@material-ui/core/Typography";

import CloseIcon from "@material-ui/icons/ArrowBack";
import SaveIcon from "@material-ui/icons/Save";

import { green, grey } from "@material-ui/core/colors";

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const useStyles = makeStyles(theme => ({
  appBar: {
    position: "relative",
    backgroundColor: grey[200],
  },

  toolbar: {
    display: "flex",
    justifyContent: "space-between",
  },

  saveButton: {
    backgroundColor: green[500],
    color: theme.palette.primary.contrastText,
  },

  title: {
    marginLeft: theme.spacing(2),
    flex: 1,
  },

  dialogBody: {
    padding: 24,
  },

  container: {
    position: "relative",
  },

  contributorNameRow: {
    display: "flex",
    justifyContent: "space-between",
  },

  prefix: {
    width: "33%",
    paddingRight: 16,
  },

  suffix: {
    width: "33%",
    paddingRight: 16,
  },

  contributorFirstName: {
    flex: 1,
    marginRight: 24,
  },
  contributorMiddleName: {
    flex: 1,
    marginRight: 24,
  },
  contributorLastName: {
    flex: 1,
  },
  contributorFullName: {
    flex: 1,
  },
}));

const ContributorDialog = ({
  contributorTypes,
  contributorRoles,
  disableSave = false,
  errors,
  fields,
  onBlur,
  onCancel,
  onFieldChange,
  onSave,
  title,
  saveText = "Save",
  visible,
}) => {
  const classes = useStyles();

  const handleFieldChange = path => event => {
    onFieldChange(path)(event.target.value);
  };

  return (
    <Dialog
      fullScreen
      open={visible}
      TransitionComponent={Transition}
      onClose={onCancel}
    >
      <AppBar className={classes.appBar}>
        <Toolbar classes={{ root: classes.toolbar }}>
          <Button color="default" startIcon={<CloseIcon />} onClick={onCancel}>
            Cancel
          </Button>
          <Typography color="primary" variant="h6" className={classes.title}>
            {title}
          </Typography>
          <Button
            variant="contained"
            classes={{ root: classes.saveButton }}
            onClick={onSave}
            disabled={disableSave}
            startIcon={<SaveIcon />}
          >
            {saveText}
          </Button>
        </Toolbar>
      </AppBar>
      <Paper square elevation={0} classes={{ root: classes.dialogBody }}>
        <div className={classes.container}>
          <div>
            <Autocomplete
              id="contributor-type"
              disableClearable={true}
              options={contributorTypes}
              getOptionLabel={option => option.displayName}
              value={
                find(propEq("id", fields.contributorType))(contributorTypes) ||
                null
              }
              onChange={(event, option) =>
                onFieldChange(["contributorType"])(option.id)
              }
              renderInput={params => (
                <TextField
                  {...params}
                  label="Type"
                  error={!!firstError(errors, "contributorType")}
                  helperText={firstError(errors, "contributorType")}
                  fullWidth
                />
              )}
            />
          </div>
          <div>
            <Autocomplete
              id="contributor-role"
              disableClearable={true}
              options={contributorRoles}
              getOptionLabel={option => option.displayName}
              value={fields.role.id ? fields.role : null}
              onChange={(event, option) => onFieldChange(["role"])(option)}
              onBlur={onBlur("role", fields.role)}
              renderInput={params => (
                <TextField
                  {...params}
                  label="Role"
                  error={!!firstError(errors, "role.id")}
                  helperText={firstError(errors, "role.id")}
                  fullWidth
                />
              )}
            />
          </div>
          {fields.contributorType === "PERSON" ? (
            <div>
              <div>
                <TextField
                  fullWidth={true}
                  className={classes.prefix}
                  type="text"
                  label="Prefix (optional)"
                  value={fields.prefix || ""}
                  onChange={handleFieldChange(["prefix"])}
                  onBlur={onBlur("prefix", fields.prefix)}
                  error={!!firstError(errors, "prefix")}
                  helperText={firstError(errors, "prefix")}
                />
              </div>
              <div>
                <TextField
                  className={classes.contributorFirstName}
                  type="text"
                  label="First"
                  value={fields.firstName || ""}
                  onChange={handleFieldChange(["firstName"])}
                  onBlur={onBlur("firstName", fields.firstName)}
                  error={!!firstError(errors, "firstName")}
                  helperText={firstError(errors, "firstName")}
                />
                <TextField
                  className={classes.contributorMiddleName}
                  type="text"
                  label="Middle"
                  value={fields.middleName || ""}
                  onChange={handleFieldChange(["middleName"])}
                  onBlur={onBlur("middleName", fields.middleName)}
                  error={!!firstError(errors, "middleName")}
                  helperText={firstError(errors, "middleName")}
                />
                <TextField
                  className={classes.contributorLastName}
                  type="text"
                  label="Last"
                  value={fields.lastName || ""}
                  onChange={handleFieldChange(["lastName"])}
                  onBlur={onBlur("lastName", fields.lastName)}
                  error={!!firstError(errors, "lastName")}
                  helperText={firstError(errors, "lastName")}
                />
              </div>
              <div>
                <TextField
                  fullWidth={true}
                  className={classes.suffix}
                  type="text"
                  label="Suffix (optional)"
                  value={fields.suffix || ""}
                  onChange={handleFieldChange(["suffix"])}
                  onBlur={onBlur("suffix", fields.suffix)}
                  error={!!firstError(errors, "suffix")}
                  helperText={firstError(errors, "suffix")}
                />
              </div>
            </div>
          ) : (
            <div>
              <TextField
                fullWidth={true}
                className={classes.contributorFullName}
                type="text"
                label="Name"
                value={fields.fullName || ""}
                onChange={handleFieldChange(["fullName"])}
                onBlur={onBlur("fullName", fields.fullName)}
                error={!!firstError(errors, "fullName")}
                helperText={firstError(errors, "fullName")}
              />
            </div>
          )}
          <div>
            <TextField
              fullWidth={true}
              type="text"
              label="Website (optional)"
              value={fields.website || ""}
              onChange={handleFieldChange(["website"])}
              onBlur={onBlur("website", fields.website)}
              error={!!firstError(errors, "website")}
              helperText={firstError(errors, "website")}
            />
          </div>
          <div>
            <TextField
              fullWidth={true}
              type="text"
              label="X handle (optional)"
              value={fields.twitterHandle || ""}
              onChange={handleFieldChange(["twitterHandle"])}
              onBlur={onBlur("twitterHandle", fields.twitterHandle)}
              error={!!firstError(errors, "twitterHandle")}
              helperText={firstError(errors, "twitterHandle")}
            />
          </div>
        </div>
      </Paper>
    </Dialog>
  );
};

export default ContributorDialog;
